'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardFooter } from '@ahafood/ui/components/card';
import { Progress } from '@ahafood/ui/components/progress';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Gift, MapPin, Zap } from 'lucide-react';
import { useParams, useRouter } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { Payload } from '../simple-store-list';
import NewOldStoreDeal from './old-deal';
import type { Tag } from '@/api/ahafood/get-deal-fn';
import type { Progress as ApiProgress } from '@/api/use-get-deal-progress';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

// Memoized time formatting function to avoid repeated calculations
const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}`;
};

const getTimeRange = (store_open_hours: any) => {
  if (!store_open_hours) return '06:00 - 22:00';

  const today = new Date().getDay();
  const dayKey = today === 0 ? '6' : (today - 1).toString();

  const todayHours = store_open_hours[dayKey];
  if (!todayHours || todayHours.length === 0) return 'Đóng cửa';

  const firstSlot = todayHours[0];
  const lastSlot = todayHours[todayHours.length - 1];

  return `${formatTime(firstSlot.start_time)} - ${formatTime(
    lastSlot.end_time
  )}`;
};

const cardVariants = {
  offscreen: {
    opacity: 0,
    x: 50,
  },
  onscreen: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      bounce: 0.4,
      duration: 0.8,
    },
  },
};

export type NewStoreDealProps = {
  payload: Payload;
  deal?: Tag[];
  deal_progress?: ApiProgress[];
  city_slug?: string;
};

export default function NewStoreDeal({
  payload,
  deal,
  deal_progress,
  city_slug,
}: NewStoreDealProps) {
  // Get locale and city from route params
  const params = useParams();
  const rawCity = Array.isArray(params.city) ? params.city[0] : params.city;
  const router = useRouter();
  const { user, isAuthenticated } = useAuthStore();
  const isMobile = useIsMobile();
  const goToOrderWebstore = useGoToOrderWebstore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const address = useAiFoodOrderStore.use.address() as Address | null;
  const cityId = useAiFoodOrderStore.use.city_id();
  const routes = useCityRoutes();

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  const convertToMenuItem = useCallback((dealItem: ApiProgress): MenuItem => {
    return {
      _id: dealItem.item_id,
      name: dealItem.item_name,
      price: dealItem.price,
      discounted_price: dealItem.discounted_price,
      image_thumb_url: dealItem.item_image_url || '',
    };
  }, []);

  const handleOrderNow = useCallback(
    async (dealItem: ApiProgress) => {
      try {
        trackEvent('click_button', {
          is_login: isAuthenticated ? 'true' : 'false',
          event_timestamp: dayjs().unix(),
          button_name: 'order_now',
          city_id: cityId,
          button_location: 'flash_deal',
          location_text: address?.address,
          lat: address?.lat,
          lng: address?.lng,
          fb_id: user?.id,
          store_name: dealItem.store_name,
          store_id: dealItem.store_id.toString(),
          store_page_id: dealItem.page_id.toString(),
        });

        const status = dealItem.status;
        if (status === 'OUT_OF_STOCK' || status === 'INACTIVE') {
          router.push(routes.storeDetail(dealItem.store_slug));
          return;
        }

        // Use goToOrderWebstore to open sheet and navigate to store detail
        await goToOrderWebstore(
          dealItem.page_id.toString(),
          dealItem.store_slug,
          '',
          convertToMenuItem(dealItem),
          dealItem.store_id?.toString() || ''
        );
        router.push(routes.storeDetail(dealItem.store_slug));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      cityId,
      address,
      user?.id,
      router,
      goToOrderWebstore,
      convertToMenuItem,
    ]
  );

  const handleClick = useCallback(
    (dealItem: ApiProgress) => {
      if (!isAuthenticated) {
        setShowLoginDialog(true);
      } else {
        void handleOrderNow(dealItem);
      }
    },
    [isAuthenticated, handleOrderNow]
  );

  const handleCardClick = useCallback(
    (dealItem: ApiProgress) => {
      router.push(routes.storeDetail(dealItem.store_slug));

      // Track card click event
      trackEvent('click_button', {
        is_login: isAuthenticated ? 'true' : 'false',
        event_timestamp: dayjs().unix(),
        button_name: 'deal_card_click',
        city_id: cityId,
        button_location: 'flash_deal',
        location_text: address?.address,
        lat: address?.lat,
        lng: address?.lng,
        fb_id: user?.id,
        store_name: dealItem.store_name,
        store_id: dealItem.store_id.toString(),
        store_page_id: dealItem.page_id.toString(),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, isAuthenticated, cityId, address, user?.id]
  );

  const { displayedDeals, hasMoreDeals } = useMemo(() => {
    // Use optional chaining and provide default empty array
    const deals = deal_progress ?? [];
    const maxDisplayItems = 10;

    // Only slice if needed
    const displayed =
      deals.length > maxDisplayItems ? deals.slice(0, maxDisplayItems) : deals;

    return {
      displayedDeals: displayed,
      hasMoreDeals: deals.length > maxDisplayItems,
    };
  }, [deal_progress]);

  const viewMoreDeals = useCallback(() => {
    router.push(`/${rawCity}/danh-sach-cua-hang-co-hot-deal`);
  }, [rawCity, router]);

  const renderDealBadge = useCallback((dealItem: ApiProgress) => {
    const { discount_config } = dealItem;

    // Set default badge text
    let badgeText = 'Deal';

    // Determine the appropriate badge text based on discount type
    if (discount_config.type === 'VALUE') {
      // For fixed amount discounts
      badgeText = `Giảm ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'FLAT') {
      // For flat price offers
      badgeText = `Đồng giá ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'PERCENT') {
      // For percentage discounts
      badgeText = `Giảm ${discount_config.value}%`;
    }

    return (
      <Badge className="absolute top-3 md:left-3 left-2 z-10 bg-gradient-to-r from-red-40 to-primary text-white font-bold md:px-3 px-1.5 md:py-1.5 py-0.5 text-xs shadow-lg rounded-full">
        <Zap className="size-3 mr-1" />
        {badgeText}
      </Badge>
    );
  }, []);

  const renderDealCard = useCallback(
    (dealItem: ApiProgress, index: number) => {
      const status = dealItem.status;
      const remaining =
        dealItem.max_use === -1 ? -1 : dealItem.max_use - dealItem.usage_count;
      const timeRange = getTimeRange(dealItem.store_open_hours);
      const isUnlimited = dealItem.max_use === -1;

      const progressPercentage = isUnlimited
        ? 50
        : Math.max(
            5,
            ((dealItem.max_use - remaining) / dealItem.max_use) * 100
          );

      return (
        <motion.div
          key={`motion-deal-${dealItem.item_id}-${index}`}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
          className="group cursor-pointer h-full"
          onClick={() => handleCardClick(dealItem)}
        >
          <Card className="overflow-hidden h-full border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white rounded-2xl">
            <div className="relative md:h-48 h-28 overflow-hidden flex-none bg-gradient-to-br from-neutral-10 to-neutral-20">
              {renderDealBadge(dealItem)}

              {dealItem.total_items && dealItem.total_items > 1 && (
                <Badge className="absolute md:top-3 top-11 md:right-3 md:left-auto left-2 z-10 bg-yellow-10 backdrop-blur-sm text-primary-60 font-semibold md:px-2 px-1 py-0.5 md:py-1 text-xs shadow-md rounded-full border border-primary-30">
                  <Gift className="size-3 mr-1" />+{dealItem.total_items - 1}{' '}
                  món
                </Badge>
              )}

              <Avatar className="size-full aspect-video rounded-none bg-neutral-10">
                <AvatarImage
                  src={
                    dealItem.item_image_url || dealItem.store_image_url || ''
                  }
                  alt={dealItem.item_name || 'Deal image'}
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <AvatarFallback className="rounded-none">
                  {dealItem.item_name || 'Deal'}
                </AvatarFallback>
              </Avatar>

              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />

              {(status === 'INACTIVE' || status === 'OUT_OF_STOCK') && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                  <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg">
                    <p className="text-neutral-70 font-bold text-sm">
                      Đã hết deal
                    </p>
                  </div>
                </div>
              )}
            </div>

            <CardContent className="md:p-3 p-2 flex-1 flex flex-col justify-between">
              <div className="md:mb-3 mb-1.5">
                <h2 className="text-lg font-bold text-neutral-90 truncate md:mb-2">
                  {dealItem.item_name}
                </h2>

                <div className="flex items-center text-sm text-neutral-60">
                  <MapPin className="size-3.5 flex-none mr-1 text-primary-50" />
                  <span className="truncate font-medium">
                    {dealItem.distance && dealItem.distance > 0 && (
                      <span className="text-primary-50 text-center">
                        {`${(dealItem.distance / 1000).toFixed(1)} km`}
                      </span>
                    )}{' '}
                    {dealItem.store_name}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="relative">
                  {isUnlimited ? (
                    <>
                      <div className="relative mb-1">
                        <Progress value={50} className="h-2.5 bg-neutral-20" />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 ease-out"
                          style={{ left: `calc(50% - 10px)` }}
                        >
                          <Zap className="size-5 fill-yellow-20 text-yellow-30" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative mb-1">
                        <Progress
                          value={progressPercentage}
                          className="h-2.5 bg-neutral-20"
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 ease-out"
                          style={{
                            left: `calc(${progressPercentage}% - 10px)`,
                          }}
                        >
                          <Zap className="size-5 fill-yellow-20 text-yellow-30" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex flex-col items-center justify-between text-sm text-neutral-60">
                  <div className="flex flex-col items-left w-full justify-between text-xs">
                    <span className="font-semibold text-primary-50">
                      {isUnlimited
                        ? 'Đang hot 🔥'
                        : `Còn ${remaining}/${dealItem.max_use} đơn`}
                    </span>
                    <div className="flex items-center text-neutral-50 text-xs">
                      <Clock className="size-3 mr-1" />
                      <span>{timeRange}</span>
                    </div>
                  </div>
                  {dealItem.discounted_price ? (
                    <div className="flex items-end w-full justify-between md:mt-1">
                      <span className="text-primary font-bold">
                        {dealItem.discounted_price.toLocaleString()} đ
                      </span>
                      <span className="line-through text-neutral-40 text-xs font-bold">
                        {dealItem.price.toLocaleString()} đ
                      </span>
                    </div>
                  ) : (
                    <>{dealItem.price.toLocaleString()} đ</>
                  )}
                </div>
              </div>
            </CardContent>

            <CardFooter className="md:px-3 md:pb-3 p-2 pt-0">
              <Button
                type="button"
                className={cn(
                  'w-full font-bold h-11 text-sm transition-all duration-300 rounded-xl',
                  status === 'OUT_OF_STOCK' || status === 'INACTIVE'
                    ? 'bg-neutral-50 hover:bg-neutral-60 text-white'
                    : 'bg-gradient-to-r from-primary to-red-40 hover:from-primary-60 hover:to-red-60 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(dealItem);
                }}
              >
                {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                  <>
                    <MapPin className="size-4 mr-2" />
                    Xem cửa hàng
                  </>
                ) : (
                  <>
                    <Zap className="size-4 mr-2" />
                    Đặt ngay
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      );
    },
    [handleCardClick, handleClick, renderDealBadge]
  );

  if (!cityId) {
    return (
      <StoreListWrapper>
        <div className="flex flex-row flex-nowrap gap-4">
          {[...Array(isMobile ? 2 : 3)].map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              className="h-[272px] w-48 flex-none rounded-xl bg-neutral-10/75 md:h-[320px] md:w-60"
            />
          ))}
        </div>
      </StoreListWrapper>
    );
  }

  if (!deal || !deal_progress) return null;

  return (
    <StoreListWrapper>
      <div className="flex flex-col gap-20">
        {deal && (
          <NewOldStoreDeal
            deals={deal}
            // isLoading={loadingDeal}
            payload={payload}
            city_slug={city_slug}
          />
        )}
        {displayedDeals.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="relative z-[5] flex space-x-2 text-2xl font-bold drop-shadow-sm md:text-5xl items-center">
                <span>🔥</span>
                <span>Flash deal</span>
              </h2>
              {displayedDeals.length > 5 && (
                <Button
                  variant="link"
                  size="lg"
                  className="-ml-4 h-14 px-4 text-lg font-bold text-neutral-70 md:h-16 [&_svg]:size-6 z-20"
                  onClick={viewMoreDeals}
                >
                  <span className="hidden md:inline-block">Xem thêm</span>
                  <ArrowRight className="size-6 md:ml-1" />
                </Button>
              )}
            </div>
            <FreeModeSwiper
              key={`deals-swiper-${displayedDeals
                .map((deal, idx) => `${deal.item_id}-${idx}`)
                .join('-')}`}
              navigation={false}
              className="w-full !overflow-visible md:h-auto"
              spaceBetween={16}
              pagination
            >
              {displayedDeals.map((dealItem, index) => (
                <FreeModeSwiper.Slide
                  key={`deal-${dealItem.item_id}-${dealItem.usage_count}-${index}`}
                  style={{
                    width: isMobile ? '180px' : 250,
                    height: 'auto',
                  }}
                >
                  {renderDealCard(dealItem, index)}
                </FreeModeSwiper.Slide>
              ))}

              {hasMoreDeals && (
                <FreeModeSwiper.Slide
                  key="view-more"
                  style={{
                    width: isMobile ? '180px' : 250,
                    height: 'auto',
                  }}
                >
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={cardVariants}
                    whileHover={{
                      scale: 1.02,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="group cursor-pointer h-full"
                  >
                    <Card
                      className="overflow-hidden h-full border-2 border-dashed border-primary-30 shadow-lg flex flex-col bg-gradient-to-br from-primary-10 to-primary-20 cursor-pointer rounded-2xl"
                      onClick={viewMoreDeals}
                    >
                      <div className="flex-1 flex flex-col items-center justify-center md:p-6 p-4 text-center">
                        <div className="size-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                          <Zap className="size-8 text-white fill-white" />
                        </div>
                        <h3 className="text-xl font-bold text-primary-60 mb-2">
                          Xem thêm deals
                        </h3>
                        <p className="text-sm text-primary-50 mb-4">
                          Còn nhiều deal khác đang chờ bạn
                        </p>
                        <Button
                          className="bg-primary-50 hover:bg-primary-60 text-white font-bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewMoreDeals();
                          }}
                        >
                          Khám phá ngay
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                </FreeModeSwiper.Slide>
              )}
            </FreeModeSwiper>
          </div>
        )}
      </div>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="flash_deal"
      />
    </StoreListWrapper>
  );
}

export function StoreListWrapper({
  children,
  payload,
}: PropsWithChildren & { payload?: any }) {
  const campaignParam = payload?.campaign_id;

  return (
    <section className="ahf-deal bg-gradient-to-b from-primary-30 to-primary-20">
      <div
        className={cn(
          'relative z-[1] mx-auto mt-10 max-w-screen-xl gap-y-6 px-4 pb-6 pt-10 md:mt-0 md:px-6 md:pt-40',
          {
            'pb-10': campaignParam,
          }
        )}
      >
        {children}
      </div>
    </section>
  );
}
